import React, { useState } from 'react';
import ConfirmModal from './ConfirmModal';

function getScoreColor(score) {
  if (score >= 8) return '#10b981';
  if (score >= 6) return '#3b82f6';
  if (score >= 4) return '#f59e0b';
  return '#ef4444';
}

function getBarColor(rank) {
  if (rank === 0) return '#10b981';
  if (rank === 1) return '#3b82f6';
  if (rank === 2) return '#f59e0b';
  return '#9ca3af';
}

function rankLabel(i) {
  if (i === 0) return { text: '🥇', cls: 'gold' };
  if (i === 1) return { text: '🥈', cls: 'silver' };
  if (i === 2) return { text: '🥉', cls: 'bronze' };
  return { text: `#${i + 1}`, cls: '' };
}

function computeSensitivity(factors, results, subjectiveScores, objectiveScores) {
  if (results.length < 2) return [];
  const winner = results[0].school;
  const insights = [];

  factors.forEach(factor => {
    const modifiedFactors = factors.map(f =>
      f.id === factor.id ? { ...f, weight: f.weight * 2 } : f
    );
    const newTotal = modifiedFactors.reduce((s, f) => s + f.weight, 0);

    const newRanking = results.map(r => {
      let total = 0;
      modifiedFactors.forEach(f => {
        const scoreMap = f.type === 'subjective' ? subjectiveScores : objectiveScores;
        const score = scoreMap[r.school]?.[f.id] ?? 5;
        total += (f.weight / newTotal) * score;
      });
      return { school: r.school, total };
    }).sort((a, b) => b.total - a.total);

    if (newRanking[0].school !== winner) {
      insights.push({
        factor: factor.name,
        newWinner: newRanking[0].school,
      });
    }
  });

  return insights;
}

function getTopFactors(result, factors) {
  return factors
    .map(f => ({ factor: f, contribution: result.breakdown[f.id]?.contribution ?? 0 }))
    .sort((a, b) => b.contribution - a.contribution)
    .slice(0, 3);
}

export default function Step5Results({
  stepIndicator,
  schools,
  factors,
  results,
  subjectiveScores,
  objectiveScores,
  onBack,
  onAdjustWeights,
  onRestart,
  onCloseVerdict,
  hpTheme = false,
}) {
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);

  if (results.length === 0) return null;

  const winner = results[0];
  const sensitivity = computeSensitivity(factors, results, subjectiveScores, objectiveScores);
  const topFactors = getTopFactors(winner, factors);

  const margin = results.length > 1
    ? (results[0].total - results[1].total).toFixed(2)
    : null;

  return (
    <div className="card" style={{ position: 'relative' }}>
      {showRestartConfirm && (
        <ConfirmModal
          title={hpTheme ? 'Start Anew?' : 'Start Over?'}
          message={
            hpTheme
              ? "The Hat's work will be undone — your schools, your scores, and this verdict shall all be forgotten."
              : 'This will clear all your schools, scores, and results. This cannot be undone.'
          }
          confirmLabel={hpTheme ? 'Yes, start anew' : 'Yes, start over'}
          cancelLabel={hpTheme ? 'No, stay' : 'Cancel'}
          onConfirm={() => { setShowRestartConfirm(false); onRestart(); }}
          onCancel={() => setShowRestartConfirm(false)}
        />
      )}
      {hpTheme && onCloseVerdict && (
        <button
          className="sh-verdict-close-btn"
          onClick={onCloseVerdict}
          aria-label="Back to verdict"
        >
          ✕
        </button>
      )}
      {stepIndicator}
      <div className="step-header">
        <h2>{hpTheme ? 'The Hat Has Chosen' : 'Step 5 — Your Results'}</h2>
        <p>
          {hpTheme
            ? "Here is my final judgment, drawn from instinct, evidence, and the weights you've given me. Study the tally if you like, but listen as well for whether my verdict rings true."
            : "Here's the full decision analysis. Scores are weighted averages on a 1–10 scale. Review the breakdown, check the sensitivity analysis, and reflect on whether this matches your gut."}
        </p>
      </div>

      {/* Winner Banner */}
      <div className="winner-banner">
        <div className="winner-trophy">🏆</div>
        <div className="winner-text">
          <h3>{hpTheme ? 'Best Fit By My Reckoning' : 'Recommended School'}</h3>
          <h2>{winner.school}</h2>
          <p>
            {hpTheme ? 'My verdict:' : 'Score:'} <strong>{winner.total.toFixed(2)}/10</strong>
            {margin && (hpTheme ? ` · Ahead of the runner-up by ${margin} points` : ` · Margin over #2: +${margin} pts`)}
          </p>
        </div>
      </div>

      {/* Rankings */}
      <div className="section-title">{hpTheme ? '📊 The Full Ordering' : '📊 Full Ranking'}</div>
      <div className="result-ranking mb-6">
        {results.map((r, i) => {
          const label = rankLabel(i);
          return (
            <div key={r.school} className={`result-card rank-${i < 3 ? i + 1 : 'other'}`}>
              <div className={`result-rank-badge ${label.cls}`}>{label.text}</div>
              <div className="result-name">{r.school}</div>
              <div className="result-bar-wrap">
                <div className="result-bar-bg">
                  <div
                    className="result-bar-fill"
                    style={{
                      width: `${(r.total / 10) * 100}%`,
                      background: getBarColor(i),
                    }}
                  />
                </div>
                <div className="result-score">{r.total.toFixed(2)}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Score Breakdown Table */}
      <div className="mb-6">
        <div className="section-title" style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>{hpTheme ? '🔢 How The Hat Counted It' : '🔢 Score Breakdown'}</span>
          <button
            className="btn btn-ghost"
            style={{ padding: '0.25rem 0.75rem', fontSize: '0.8rem' }}
            onClick={() => setShowBreakdown(!showBreakdown)}
          >
            {showBreakdown ? (hpTheme ? 'Hide' : 'Hide') : (hpTheme ? 'Show' : 'Show')} {hpTheme ? 'Ledger' : 'Table'}
          </button>
        </div>

        {showBreakdown && (
          <div style={{ overflowX: 'auto' }}>
            <table className="breakdown-table">
              <thead>
                <tr>
                  <th>{hpTheme ? 'Measure (share of judgment)' : 'Factor (weight %)'}</th>
                  {results.map(r => (
                    <th key={r.school}>{r.school}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {factors.map(factor => {
                  const scores = results.map(r => r.breakdown[factor.id]?.score ?? 5);
                  const maxFactorScore = Math.max(...scores);
                  const pct = ((factor.weight / factors.reduce((s, f) => s + f.weight, 0)) * 100).toFixed(0);
                  return (
                    <tr key={factor.id}>
                      <td>
                        {factor.name}
                        <span style={{ color: '#9ca3af', fontWeight: 400, marginLeft: '0.4rem' }}>
                          ({pct}%)
                        </span>
                      </td>
                      {results.map(r => {
                        const bd = r.breakdown[factor.id];
                        const score = bd?.score ?? 5;
                        const isBest = score === maxFactorScore;
                        return (
                          <td key={r.school} className={isBest ? 'best-cell' : ''}>
                            <div className="cell-score" style={{ color: getScoreColor(score) }}>
                              {score}/10
                            </div>
                            <div className="cell-contrib">
                              +{((bd?.contribution ?? 0)).toFixed(2)}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                <tr>
                  <td>{hpTheme ? 'Final Tally' : 'Total Score'}</td>
                  {results.map(r => (
                    <td key={r.school}>
                      <div className="cell-score" style={{ fontSize: '1rem', color: getScoreColor(r.total) }}>
                        {r.total.toFixed(2)}
                      </div>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Why Winner Won */}
      <div className="mb-6">
        <div className="section-title">{hpTheme ? `💡 Why I Chose ${winner.school}` : `💡 Why ${winner.school} Won`}</div>
        <div className="explanation-items">
          {topFactors.map(({ factor, contribution }) => (
            <div key={factor.id} className="explanation-item">
              <div className="explanation-dot" style={{ background: getScoreColor(winner.breakdown[factor.id]?.score ?? 5) }} />
              <span>
                {hpTheme ? (
                  <><strong>{factor.name}</strong> weighed strongly in its favor: <strong>{contribution.toFixed(2)} points</strong> from a score of <strong>{winner.breakdown[factor.id]?.score ?? 5}/10</strong>, carrying {((factor.weight / factors.reduce((s, f) => s + f.weight, 0)) * 100).toFixed(0)}% of my judgment.</>
                ) : (
                  <><strong>{factor.name}</strong> contributed <strong>{contribution.toFixed(2)} pts</strong> (score: <strong>{winner.breakdown[factor.id]?.score ?? 5}/10</strong>, weight: {((factor.weight / factors.reduce((s, f) => s + f.weight, 0)) * 100).toFixed(0)}%)</>
                )}
              </span>
            </div>
          ))}
          {results.length > 1 && (
            <div className="explanation-item">
              <div className="explanation-dot" style={{ background: '#6b7280' }} />
              <span>
                {hpTheme ? (
                  <>It finished ahead of <strong>{results[1].school}</strong> by <strong>{margin} points</strong> out of 10, making this {parseFloat(margin) < 0.3 ? 'a razor-thin choice' : parseFloat(margin) < 0.7 ? 'a narrow but meaningful edge' : 'a clear and confident selection'}.</>
                ) : (
                  <>Margin over <strong>{results[1].school}</strong>: <strong>+{margin} points</strong> out of 10 — {parseFloat(margin) < 0.3 ? 'very close race' : parseFloat(margin) < 0.7 ? 'modest lead' : 'clear advantage'}.</>
                )}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Sensitivity Analysis */}
      <div className="mb-6">
        <div className="section-title">{hpTheme ? '⚖️ If I Changed My Emphasis' : '⚖️ Sensitivity Analysis'}</div>
        <p className="text-sm text-muted mb-3">
          {hpTheme ? 'Suppose I cared twice as much about a single measure. Would my choice change?' : 'What if you doubled the weight of a single factor? Would the winner change?'}
        </p>
        {sensitivity.length === 0 ? (
          <p className="sensitivity-none">
            {hpTheme ? `${winner.school} remains my choice no matter which single measure I prize twice as highly. A sturdy verdict, that.` : `${winner.school} wins regardless of which factor you double-weight. It's a robust choice.`}
          </p>
        ) : (
          <div className="sensitivity-list">
            {sensitivity.map((s, i) => (
              <div key={i} className="sensitivity-item">
                {hpTheme ? <>If I gave <strong>{s.factor}</strong> twice the weight, <strong>{s.newWinner}</strong> would take the lead instead.</> : <>If <strong>{s.factor}</strong> mattered twice as much, <strong>{s.newWinner}</strong> would win instead.</>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reflection */}
      <div className="reflection-card mb-6">
        <p>{hpTheme ? "Now answer me honestly: does this choice feel right? If not, then there's some hidden quality still rustling about in your thoughts." : 'Does this result match your intuition? If not, something important may be missing from the model.'}</p>
        <div className="reflection-actions">
          <button className="btn btn-secondary" onClick={onAdjustWeights}>
            {hpTheme ? '✏️ Change What Matters' : '✏️ Adjust Weights'}
          </button>
          <button className="btn btn-secondary" onClick={onBack}>
            {hpTheme ? '← Revisit The Scores' : '← Edit Scores'}
          </button>
          <button className="btn btn-ghost" onClick={() => setShowRestartConfirm(true)}>
            {hpTheme ? '🔄 Start The Sorting Anew' : '🔄 Start Over'}
          </button>
        </div>
      </div>

      <div className="step-footer">
        <button className="btn btn-secondary" onClick={onBack}>{hpTheme ? '← Back' : '← Back'}</button>
        <div style={{ fontSize: '0.8rem', color: '#9ca3af', textAlign: 'right' }}>
          {hpTheme ? `Based on ${factors.length} measures across ${schools.length} schools` : `Based on ${factors.length} factors across ${schools.length} schools`}
        </div>
      </div>
    </div>
  );
}
